import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminAvatar from "../../components/admin/AdminAvatar";
import BookingDetailsModal from "../../components/admin/BookingDetailsModal";
import {
  useAdminUserDetailQuery,
  useReleaseBookingPayoutMutation,
  useSuspendUserMutation,
  useUnsuspendUserMutation,
} from "../../api/query/useAdminQueries";
import {
  BOOKING_STATUS_STYLES,
  formatNaira,
  formatNairaDecimal,
} from "../../data/adminUsers";

const SORT_OPTIONS = [
  { value: "recent", label: "Most recent" },
  { value: "amount", label: "Amount" },
  { value: "service", label: "Service" },
];

const BOOKINGS_PER_PAGE = 6;

const sumSubtotals = (items = []) =>
  items.reduce((sum, item) => sum + (Number(item?.subtotal) || 0), 0);

const formatAddOnItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return "—";

  return items
    .map((item) => {
      const quantity = Number(item?.quantity) || 0;
      const label = item?.label || item?.key || "Add-on";
      return quantity > 0 ? `${quantity} ${label}` : label;
    })
    .join(", ");
};

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-gray-900">{children}</p>
    </div>
  );
}

function mapBookingStatus(status) {
  switch (status) {
    case "completed":
      return "Completed";
    case "cancelled":
      return "Canceled";
    case "rejected":
      return "Canceled";
    case "failed_payment":
      return "Failed Payment";
    case "accepted":
      return "Confirmed";
    case "pending_assistant_action":
      return "Pending";
    case "assistant_arrived":
    case "trip_started":
      return "Active";
    default:
      return "Pending";
  }
}

function formatLastActivity(isoString) {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB");
}

function AdminUserDetail() {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [sortBy, setSortBy] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAdminUserDetailQuery(id);
  const suspendMutation = useSuspendUserMutation();
  const unsuspendMutation = useUnsuspendUserMutation();
  const releasePayoutMutation = useReleaseBookingPayoutMutation();

  const userData = data?.data?.user;
  const bookingMetrics = data?.data?.bookingMetrics || {};

  const bookingHistory = useMemo(() => {
    const rawBookingHistory = data?.data?.bookingHistory || [];
    const list = rawBookingHistory.map((b) => {
      const vehicleCost = sumSubtotals(b.escortVehicles);
      const securityCost = sumSubtotals(b.securityPersonnel);
      const total = Number(b.amountExpected) || Number(b.amount) || 0;
      const baseTripCost = Math.max(0, total - vehicleCost - securityCost);

      return {
        id: b.id,
        name: b.assistantName,
        service: "Pulo Assistant",
        location: "—",
        date: b.arrivalDate,
        amount: total,
        status: mapBookingStatus(b.status),
        // The display label folds several statuses together, so the raw one
        // comes along for the actions that depend on it.
        rawStatus: b.status,
        details: {
          type: "Pulo Assistant",
          assistant: {
            name: b.assistantName || "Pulo Assistant",
            carType: "—",
            carColor: "—",
            numberPlate: "—",
          },
          trip: {
            participants: b.touristsCount || "—",
            location: "—",
            checkInDate: b.arrivalDate || "—",
            checkOutDate: b.departureDate || "—",
            itinerary: b.itinerary || "—",
          },
          logistics: {
            securityOfficers: formatAddOnItems(b.securityPersonnel),
            convoyVehicles: formatAddOnItems(b.escortVehicles),
          },
          payment: {
            baseTripCost,
            vehicleCost,
            securityCost,
            total,
          },
        },
      };
    });
    if (sortBy === "amount") {
      list.sort((a, b) => b.amount - a.amount);
    } else if (sortBy === "service") {
      list.sort((a, b) => a.service.localeCompare(b.service));
    }
    return list;
  }, [data, sortBy]);

  const totalBookings = bookingHistory.length;
  const totalPages = Math.max(1, Math.ceil(totalBookings / BOOKINGS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const bookingStart = (currentPage - 1) * BOOKINGS_PER_PAGE;
  const pagedBookings = bookingHistory.slice(
    bookingStart,
    bookingStart + BOOKINGS_PER_PAGE
  );

  // Pays an assistant for a trip that ran but never collected both
  // confirmations. Money movement, so it asks first and records why.
  const handleReleasePayout = () => {
    const bookingId = selectedBooking?.id;
    if (!bookingId) return;

    const assistantName = selectedBooking?.name || "the assistant";
    if (
      !window.confirm(
        `Release the payout held on this booking to ${assistantName}? This closes the trip and pays out whatever it still owes.`
      )
    ) {
      return;
    }

    const reason = window.prompt("Why is this payout being released? (optional)") || "";
    releasePayoutMutation.mutate(
      { bookingId, payload: reason ? { reason } : {} },
      {
        onSuccess: (response) => {
          const released = Number(
            response?.data?.totalReleased ?? response?.totalReleased ?? 0
          );
          toast.success(
            released > 0
              ? `${formatNairaDecimal(released)} released to ${assistantName}.`
              : "Booking closed. It had nothing left to release."
          );
          setSelectedBooking(null);
        },
        onError: (err) => {
          toast.apiError(err, "Failed to release this payout.");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-500">Loading user details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!userData) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-600">User not found.</p>
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="mt-4 rounded-lg bg-[#570033] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#61013B]"
          >
            Back to Users
          </button>
        </div>
      </AdminLayout>
    );
  }

  const isActive = userData.status === "Active";

  const handleToggleStatus = () => {
    if (isActive) {
      suspendMutation.mutate(id, {
        onSuccess: () => {
          toast.success(`${userData.fullName} has been suspended.`);
        },
        onError: () => {
          toast.error("Failed to suspend user.");
        },
      });
    } else {
      unsuspendMutation.mutate(id, {
        onSuccess: () => {
          toast.success(`${userData.fullName} has been unsuspended.`);
        },
        onError: () => {
          toast.error("Failed to unsuspend user.");
        },
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-6 py-4 shadow-sm">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-base font-semibold text-gray-900 transition hover:text-[#570033]"
          >
            <ArrowLeft className="h-5 w-5" />
            View User
          </button>

          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={suspendMutation.isPending || unsuspendMutation.isPending}
            className="rounded-lg bg-[#570033] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#61013B] disabled:opacity-50"
          >
            {suspendMutation.isPending || unsuspendMutation.isPending
              ? "Updating..."
              : isActive
              ? "Suspend User"
              : "Unsuspend User"}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
          <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900">Personal Information</h2>
            <div className="mt-4 flex justify-center border-b border-gray-100 pb-6">
              <AdminAvatar
                src={userData.profilePicture}
                name={userData.fullName}
                className="h-24 w-24 rounded-full object-cover text-3xl"
              />
            </div>
            <div className="mt-6 space-y-5">
              <Field label="Full Name">{userData.fullName}</Field>
              <Field label="Email Address">{userData.email}</Field>
              <Field label="Phone Number">{userData.mobile || "N/A"}</Field>
              <Field label="Status">{userData.status}</Field>
              <Field label="Email Verified">{userData.isEmailVerified ? "Yes" : "No"}</Field>
              <Field label="Total Bookings">{bookingMetrics.totalBookings}</Field>
              <Field label="Total Spending">
                {formatNairaDecimal(bookingMetrics.totalSpending)}
              </Field>
              <Field label="Last Activity">{formatLastActivity(userData.lastLogin)}</Field>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
              <h2 className="text-lg font-bold text-gray-900">Booking History</h2>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setSortOpen((prev) => !prev)}
                  className="flex items-center gap-2 rounded-full border border-[#C9D3E2] bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:border-[#570033]"
                >
                  Sort by
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
                {sortOpen ? (
                  <div className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSortBy(option.value);
                          setSortOpen(false);
                          setPage(1);
                        }}
                        className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-gray-50 ${
                          sortBy === option.value
                            ? "font-semibold text-[#570033]"
                            : "text-gray-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Booking Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Service
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBookings.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                        No bookings found.
                      </td>
                    </tr>
                  ) : (
                    pagedBookings.map((booking) => (
                      <tr
                        key={booking.id}
                        className="cursor-pointer border-b border-gray-100 transition hover:bg-gray-50"
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {booking.name}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-600">
                          {booking.service}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {booking.location}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-600">
                          {booking.date}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatNaira(booking.amount)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                              BOOKING_STATUS_STYLES[booking.status] ||
                              "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {booking.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="flex flex-col gap-4 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  Showing {bookingStart + 1} to{" "}
                  {Math.min(bookingStart + BOOKINGS_PER_PAGE, totalBookings)} of{" "}
                  {totalBookings} results
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                    (pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition ${
                          pageNumber === currentPage
                            ? "bg-[#570033] text-white"
                            : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    )
                  )}

                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <BookingDetailsModal
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onReleasePayout={handleReleasePayout}
        releasing={releasePayoutMutation.isPending}
        onContactUser={() => toast.success(`Contacting ${userData.fullName}.`)}
        onContactParty={() =>
          toast.success(`Contacting ${selectedBooking?.name || "party"}.`)
        }
      />
    </AdminLayout>
  );
}

export default AdminUserDetail;
